import { useCallback } from 'react';
import { useApi } from '../../hooks/useApi';
import { getStatsOccupancy } from '../../lib/api';
import { formatPct, pctDelta } from '../../lib/format';
import { KpiCard } from '../../components/stats/KpiCard';
import { BarChart } from '../../components/stats/BarChart';
import { BreakdownList } from '../../components/stats/BreakdownList';
import { SectionFrame } from '../../components/stats/SectionFrame';
import { PS, T } from '../../lib/theme';
import type { OccupancyStats } from '../../lib/types';

function formatHours(value: number): string {
  return `${value.toFixed(2)} occupied table-hours`;
}

function formatOccupancyPct(value: number): string {
  // Occupancy is often fractional for a sparse period. Preserve that server
  // value instead of rounding a nonzero utilization down to a misleading 0%.
  return `${value.toLocaleString('en-US', {
    minimumFractionDigits: value % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  })}%`;
}

export function OccupancySection({ start, end }: { start: string; end: string }) {
  const loader = useCallback(
    (signal: AbortSignal) => getStatsOccupancy(start, end, signal),
    [start, end],
  );
  const { data, loading, error } = useApi<OccupancyStats>(loader);

  return (
    <SectionFrame title="Occupancy" loading={loading} error={error}>
      {data && (
        <>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
            <KpiCard
              label="Total occupied utilization"
              value={formatOccupancyPct(data.occupied_utilization_pct)}
              delta={pctDelta(data.occupied_utilization_pct, data.prev_occupied_utilization_pct)}
            />
          </div>
          <div>
            <div style={{ ...T.label, marginBottom: 6 }}>Busiest hours: occupied table-hours</div>
            <div style={{ ...T.caption, marginBottom: 6 }}>
              Busiest hours: occupied table-hours from confirmed use, not booking start-hour counts. Hours in Asia/Tbilisi: 00:00 through 23:00.
            </div>
            <BarChart
              data={data.occupied_by_hour}
              color={PS.info}
              format={formatHours}
              showXAxis
              labelFormat={(label) => `${label}:00`}
              ariaLabel="Occupied table-hours by venue hour"
            />
          </div>
          <div>
            <div style={{ ...T.label, marginBottom: 6 }}>Total occupied utilization by table</div>
            <BreakdownList data={data.occupied_by_table} format={(v) => formatPct(v)} />
          </div>
          <div style={{ ...T.caption }}>
            {data.capacity_basis} Historical reports use the current weekday schedule and active tables because schedule history is unavailable.
          </div>
          <div>
            <div style={{ ...T.label, marginBottom: 6 }}>Daily occupied hours by table</div>
            <div style={{ ...T.caption, marginBottom: 10 }}>
              Each bar is one Asia/Tbilisi date. Select or focus a bar for its exact occupied table-hours.
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 14 }}>
              {data.by_table_daily_hours.map((table) => (
                <section
                  key={table.table_id}
                  aria-label={`Daily occupied hours for ${table.label}`}
                  style={{ background: PS.white, border: `1px solid ${PS.borderLight}`, borderRadius: 6, padding: 12 }}
                >
                  <h3 style={{ ...T.ui, margin: '0 0 6px', color: PS.nearBlack }}>{table.label}</h3>
                  <div style={{ ...T.caption, marginBottom: 8 }}>
                    {table.total_hours.toFixed(2)} occupied table-hours total · {table.average_hours_per_day.toFixed(2)} occupied table-hours/day
                  </div>
                  <BarChart
                    data={table.daily.map((day) => ({ label: day.date, value: day.value }))}
                    color={PS.tealDeep}
                    format={formatHours}
                    showXAxis
                    axisLabelFormat={(date) => date.slice(5)}
                    ariaLabel={`Daily occupied table-hours for ${table.label}`}
                  />
                </section>
              ))}
            </div>
          </div>
        </>
      )}
    </SectionFrame>
  );
}
